import React, { useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import { green, red } from '@mui/material/colors';
import Button from '@mui/material/Button';
import { useSnackbar } from 'notistack';
import { useTranslate, useDataProvider } from 'react-admin';
import SearchIcon from '@mui/icons-material/Search';
import { GET_ONE, GET_MANY } from 'ra-core';
import { useFormContext } from "react-hook-form";

const styles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: '1rem'
    },
    wrapper: {
        margin: theme.spacing(1),
        position: 'relative',
    },
    buttonSuccess: {
        backgroundColor: green[500],
        '&:hover': {
            backgroundColor: green[700],
        },
    },
    buttonError: {
        backgroundColor: red[500],
        '&:hover': {
            backgroundColor: red[700],
        },
    },
    buttonProgress: {
        color: green[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12,
    },
    close: {
        padding: theme.spacing(0.5),
    },
    iconSmall: {
        fontSize: 24,
    },
}));

function GetTaskButton() {
    const classes = styles();

    const [buttonClass, setButtonClass] = useState(clsx("NoClass"));
    const [loading, setLoading] = useState(false);

    const { enqueueSnackbar } = useSnackbar();
    const dataProvider = useDataProvider();
    const translate = useTranslate();

    const { getValues } = useFormContext();

    const getDerivedStateFromProps = (nextProps, state) => {
        if (nextProps.buttonClass && nextProps.buttonClass === "Clear") {
            setButtonClass(clsx("NoClass"));
        }
    }

    const showMessage = (message, level) => {
        enqueueSnackbar(message, {
            variant: level,
            anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'center',
            },
            autoHideDuration: 3000,
        });
    }

    const handleButtonClick = async () => {
        let record = getValues();
        const workrequestid = record.workrequestid;
        var cod_nmu;
        if (!record.id && record.serial_number_tim !== "") {
            cod_nmu = record.serial_number_tim.substring(5, 11);
        }
        else {
            cod_nmu = record.cod_nmu;
        }

        if (!cod_nmu || isNaN(cod_nmu)) {
            showMessage(translate('tim.gettask.wrong_nmu'), 'warning');
            return;
        }

        let NMUGestitoFilter = await dataProvider(GET_ONE, 'settings', { name: 'NMUGestitoFilter' });
        let filter = NMUGestitoFilter.data.value;
        filter['cod_nmu'] = cod_nmu;
        var nomenclatoresCount = await dataProvider(GET_MANY, 'nomenclatores', { "filter": filter })
            .then(
                response => {
                    return response.total ? response.total : undefined;
                })
            .catch(error => {
                console.log(error);
                return undefined;
            });
        if (!nomenclatoresCount || nomenclatoresCount === 0) {
            showMessage(translate('tim.gettask.wrong_nmu'), 'warning');
            return;
        }
        //2147483647 is the maximum value for Workrequestid in Click anything higher than that throws an exception
        else if (workrequestid !== "" && workrequestid > 0 && workrequestid <= 2147483647) {
            setLoading(true);
            showMessage(translate('tim.gettask.getting_info'), 'info');

            //Call with token [PROD]
            dataProvider(GET_ONE, 'tasks', { workrequestid: workrequestid, nmu: cod_nmu })
                .then(response => {
                    let data = response.data;
                    //Task not found (REQ018 - CASO4)
                    if (data.errore) {
                        setButtonClass(clsx(classes.buttonError));
                        setLoading(false);
                        showMessage(data.errore, 'error');
                        document.getElementById('workrequestid').dataset.isSuccess = false;

                        // if (data.errore.indexOf("Completato con Successo") === -1)
                        // {
                        //   this.setInputValue(document.getElementById('impianto'), 'WR NON RICONOSCIUTA');
                        //   this.setInputValue(document.getElementById('codice_impresa_name'), 'WR NON RICONOSCIUTA');
                        // }
                        // else
                        // {
                        //   this.setInputValue(document.getElementById('impianto'), '');
                        //   this.setInputValue(document.getElementById('codice_impresa_name'), '');
                        // }

                        //Status and Anomalia are set in ValidateAndSave.js if impianto contains WR NON RICONOSCIUTA
                    }
                    else if (data.ExternalRefId) {
                        if (data.nwfmTask) {
                            showMessage(translate('tim.gettask.success_nwfmTask', { id: document.getElementById('workrequestid').value }), 'success');
                        }
                        else {
                            showMessage(translate('tim.gettask.success', { id: document.getElementById('workrequestid').value }), 'success');
                        }
                        setInputValue(document.getElementById('externalrefid'), data.ExternalRefId);
                        data.Telefonico && setInputValue(document.getElementById('impianto'), data.Telefonico);
                        data.AssegnatoA && setInputValue(document.getElementById('codice_impresa_name'), data.AssegnatoA);
                        data.TipoTask && setInputValue(document.getElementById('tipo_task'), data.TipoTask);
                        data.WRProdotti !== undefined && setInputValue(document.getElementById('wr_prodotti'), data.WRProdotti);
                        data.CodProgetto && setInputValue(document.getElementById('cod_proj'), data.CodProgetto);
                        data.TipoCliente && setInputValue(document.getElementById('client_type'), data.TipoCliente);
                        data.ResourceId && setInputValue(document.getElementById('resource_id'), data.ResourceId);
                        data.Causale && setInputValue(document.getElementById('causale.codice'), data.Causale);
                        data.DataCompletamentoWR && setInputValue(document.getElementById('data_completamento_wr'), data.DataCompletamentoWR);
                        data.CodiceISP && setInputValue(document.getElementById('codice_isp'), data.CodiceISP);
                        data.CheckNMU ? setInputValue(document.getElementById('check_NMU'), data.CheckNMU) : setInputValue(document.getElementById('check_NMU'), '');
                        data.DescrizioneImpresa && setInputValue(document.getElementById('descrizione_impresa'), data.DescrizioneImpresa);
                        data.ListaNMU ? setInputValue(document.getElementById('lista_NMU'), data.ListaNMU) : setInputValue(document.getElementById('lista_NMU'), '');
                        data.TSYS && setInputValue(document.getElementById('tsys'), data.TSYS);
                        data.FlagOlo && setInputValue(document.getElementById('flag_olo'), data.FlagOlo);
                        data.Area && setInputValue(document.getElementById('area'), data.Area);
                        data.ProgettoConScarico && setInputValue(document.getElementById('progetto_con_scarico'), data.ProgettoConScarico);
                        data.TipoNMU && setInputValue(document.getElementById('tipo_nmu'), data.TipoNMU);
                        data.CodiceRiscontro && setInputValue(document.getElementById('codice_riscontro'), data.CodiceRiscontro);
                        data.PostDelivery ? setInputValue(document.getElementById('post_delivery'), data.PostDelivery)
                            : setInputValue(document.getElementById('post_delivery'), '');
                        data.EquivalenzaProdApplicabile ? setInputValue(document.getElementById('equivalenza_prod_applicabile'), data.EquivalenzaProdApplicabile)
                            : setInputValue(document.getElementById('equivalenza_prod_applicabile'), '');

                        //This needs to be the last set to be performed, for some reason we noticed that on the first code execution this set is not done if done above....
                        document.getElementById('workrequestid').dataset.isSuccess = true;
                        document.getElementById('workrequestid').dataset.gettaskres = document.getElementById('workrequestid').value;

                        setButtonClass(clsx(classes.buttonSuccess));
                        setLoading(false);
                    }
                    else {
                        setButtonClass(clsx(classes.buttonError));
                        setLoading(false);
                        showMessage(translate('tim.gettask.generic_error'), 'error');
                        console.log('Error performing getTask fetch: ' + JSON.stringify(data));
                        document.getElementById('workrequestid').dataset.isSuccess = false;
                    }
                })
                .catch(error => {
                    setButtonClass(clsx(classes.buttonError));
                    setLoading(false);
                    showMessage(translate('tim.gettask.generic_error'), 'error');
                    console.log('Error performing getTask fetch: ' + error.message);
                    document.getElementById('workrequestid').dataset.isSuccess = false;
                });
        }
        else {
            if (workrequestid === "")
                showMessage(translate('tim.gettask.missing_workrequestid'), 'warning');
            else
                showMessage(translate('tim.gettask.minmax_workrequestid'), 'warning');
        }
    }

    const setInputValue = (element, value) => {
        const valueSetter = Object.getOwnPropertyDescriptor(element, 'value') ? Object.getOwnPropertyDescriptor(element, 'value').set : undefined;
        const prototype = Object.getPrototypeOf(element);
        const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

        if (valueSetter && valueSetter !== prototypeValueSetter) {
            prototypeValueSetter.call(element, value);
        } else {
            valueSetter.call(element, value);
        }
        element.dispatchEvent(new Event('input', { bubbles: true }));
    }

    return (
        <div className={classes.root}>
            <div className={classes.wrapper}>
                <Button
                    variant="contained"
                    color="primary"
                    className={buttonClass}
                    disabled={loading}
                    onClick={handleButtonClick}
                    buttonClass={buttonClass}
                >
                    <SearchIcon className={clsx(classes.iconSmall)} />
                </Button>
                {loading && <CircularProgress size={24} className={classes.buttonProgress} />}
            </div>
        </div>

    );
}

export default GetTaskButton;